import { TranslateService } from '@ngx-translate/core';
import { Config, DriveStep, Popover } from 'driver.js';

import { BuildPopoverParams } from '../types/build-popover-params.type';
import { GuideDriverAdapterService } from './guide-driver-adapter.service';

export abstract class AbstractGuideService {
  protected readonly defaultPopoverConfig: Pick<Popover, 'side' | 'align'> = {
    side: 'left',
    align: 'start',
  };

  protected constructor(
    protected readonly guideDriverAdapterService: GuideDriverAdapterService,
    protected readonly translateService: TranslateService,
  ) {}

  protected translate(key: string): string {
    return this.translateService.instant(key);
  }

  protected buildAnchorSelector(anchor: string): string {
    return `[data-guide-anchor="${anchor}"]`;
  }

  protected buildPopover(params: BuildPopoverParams): Popover {
    return {
      ...this.defaultPopoverConfig,
      title: this.translate(params.titleKey),
      description: this.translate(params.descriptionKey),
      ...params.overrides,
    };
  }

  protected startGuide(config: Config): void {
    this.guideDriverAdapterService.setConfig(config);
    this.guideDriverAdapterService.start();
  }

  protected startGuideAt(stepIndex: number): void {
    this.guideDriverAdapterService.start(stepIndex);
  }

  protected setSteps(steps: DriveStep[]): void {
    this.guideDriverAdapterService.setSteps(steps);
  }

  protected refreshGuide(): void {
    this.guideDriverAdapterService.refresh();
  }

  protected moveNext(): void {
    this.guideDriverAdapterService.moveNext();
  }

  protected movePrevious(): void {
    this.guideDriverAdapterService.movePrevious();
  }

  protected moveTo(stepIndex: number): void {
    this.guideDriverAdapterService.moveTo(stepIndex);
  }

  protected isStepAvailable(step: DriveStep): boolean {
    if (!step.element) {
      return true;
    }

    if (typeof step.element !== 'string') {
      return true;
    }

    const element = document.querySelector(step.element);

    return element instanceof HTMLElement && this.isElementVisible(element);
  }

  protected isElementVisible(element: HTMLElement): boolean {
    return Boolean(element.offsetParent || element.getClientRects().length);
  }

  protected destroyGuideSafely(): void {
    this.guideDriverAdapterService.destroySafely();
  }
}
